import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";

import DataTable from "../../../../../Common/DataTable/DataTable";
import DataTableFooter from "../../../../../Common/DataTableFooter/DataTableFooter";
import DataTableHeader from "../../../../../Common/DataTableHeader/DataTableHeader";
import { REACT_APP_API_URL } from "../../../../../env";
import { fetchManagerBlockingHistory } from "../../../../../Redux/slice/features/blockingSlice";
import { setCurrentPage } from "../../../../../Redux/slice/features/paginationSilce";

const managerBlockingColumns = [
  {
    Header: "Room Type",
    accessor: "roomTypeName",
  },
  {
    Header: "Rooms Blocked",
    accessor: "newBlockRoom",
  },
  {
    Header: "From Date",
    accessor: "blockFromDate",
  },
  {
    Header: "To Date",
    accessor: "blockToDate",
  },
  {
    Header: "Customer Name",
    accessor: "customerName",
  },
];

const normalizeBlockingRows = (rows) => {
  if (!Array.isArray(rows)) return [];

  return rows.map((item) => ({
    hotelName: item.PropertyName || item.UnitName || item.HotelName || "",
    customerName: item.CustomerName || item.customerName || "",
    blockFromDate: item.BlockFromDate || item.blockFromDate || "",
    blockToDate: item.BlockToDate || item.blockToDate || "",
    roomTypeName: item.RoomTypeName || item.HotelRoomConfigType || item.RoomType || "",
    blockedRooms: item.BlockRooms ?? item.blockedRooms ?? "",
    newBlockRoom: item.newBlockRoom ?? item.NewBlockRoom ?? item.NewBlockedRooms ?? "",
    totalCount: Number(item.TotalCount ?? item.totalCount ?? 0),
  }));
};

const RoomBlockingList = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { data, status, error } = useSelector((state) => state.blockings);
  const currentPage = useSelector((state) => state.pagination.currentPage);
  const itemsPerPage = useSelector((state) => state.pagination.itemsPerPage);

  const managerHotelId = user?.user?.PropertyID;
  const [roomTypes, setRoomTypes] = useState([]);
  const [filters, setFilters] = useState({
    roomTypeId: "",
    fromDate: "",
    toDate: "",
    customerName: "",
  });
  const [debouncedCustomer, setDebouncedCustomer] = useState(filters.customerName);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCustomer(filters.customerName);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.customerName]);

  useEffect(() => {
    const fetchRoomTypes = async () => {
      if (!managerHotelId) return;

      try {
        const response = await axios.post(
          `${REACT_APP_API_URL}api/dashboard/admin/get-room-config`,
          {
            P_INTPROPERTYID: managerHotelId,
            P_CHARACTION: "All",
          },
          { withCredentials: true },
        );

        const activeRoomTypes = (response.data.data || [])
          .filter((room) => room.Status === undefined || Number(room.Status) === 1)
          .map((room) => ({
            id: room.RoomConfigID || room.HotelRoomConfigID,
            name: room.RoomType || room.RoomConfigName || room.HotelRoomConfigType,
          }))
          .filter((room) => room.id && room.name);

        setRoomTypes(activeRoomTypes);
      } catch (err) {
        console.error("Failed to fetch manager room types:", err);
        setRoomTypes([]);
      }
    };

    fetchRoomTypes();
  }, [managerHotelId]);

  useEffect(() => {
    dispatch(
      fetchManagerBlockingHistory({
        roomTypeId: filters.roomTypeId || undefined,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        customerName: debouncedCustomer,
        pageNo: currentPage,
        pageSize: itemsPerPage,
      }),
    );
  }, [
    dispatch,
    filters.roomTypeId,
    filters.fromDate,
    filters.toDate,
    debouncedCustomer,
    currentPage,
    itemsPerPage,
  ]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [dispatch, filters.roomTypeId, filters.fromDate, filters.toDate, debouncedCustomer]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [dispatch, itemsPerPage]);

  const dataT = useMemo(() => normalizeBlockingRows(data), [data]);
  const totalRecords = dataT[0]?.totalCount || data?.[0]?.TotalCount || 0;
  const totalPages = Math.ceil(totalRecords / itemsPerPage) || 1;
  const isLoading = status === "pending" || status === "loading" || status === "idle";
  const isError = status === "rejected" || status === "failed";

  const clearFilters = () => {
    setFilters({
      roomTypeId: "",
      fromDate: "",
      toDate: "",
      customerName: "",
    });
    dispatch(setCurrentPage(1));
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      dispatch(setCurrentPage(currentPage - 1));
    }
  };

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row g-3">
        <div className="col-sm-12">
          <div className="d-flex align-items-center justify-content-between flex-wrap">
            <h3 className="fw-bold mb-0">Blocked Rooms</h3>
          </div>
        </div>

        <div className="col-sm-12">
          <div className="card">
            <div className="card-body">
              {isLoading && <h4>Loading...</h4>}
              {isError && <p>Error: {error}</p>}
              {status === "fulfilled" && (
                <>
                  <div className="col-sm-12 mb-4">
                    <div className="card">
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-2">
                            <label className="form-label">From Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filters.fromDate}
                              onChange={(e) =>
                                setFilters({ ...filters, fromDate: e.target.value })
                              }
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">To Date</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filters.toDate}
                              min={filters.fromDate}
                              onChange={(e) => {
                                if (filters.fromDate && e.target.value < filters.fromDate) {
                                  alert("To Date cannot be before From Date");
                                  return;
                                }

                                setFilters({ ...filters, toDate: e.target.value });
                              }}
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Room Type</label>
                            <select
                              className="form-select"
                              value={filters.roomTypeId}
                              onChange={(e) =>
                                setFilters({ ...filters, roomTypeId: e.target.value })
                              }
                            >
                              <option value="">-- Select Room Type --</option>
                              {roomTypes.map((room) => (
                                <option key={room.id} value={room.id}>
                                  {room.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-3">
                            <label className="form-label">Customer Name</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter customer name"
                              value={filters.customerName}
                              onChange={(e) =>
                                setFilters({ ...filters, customerName: e.target.value })
                              }
                            />
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-secondary w-100" onClick={clearFilters}>
                              Clear Filters
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <DataTableHeader columns={managerBlockingColumns} data={dataT} />
                  <DataTable columns={managerBlockingColumns} data={dataT} enablePagination={false} />
                  <DataTableFooter
                    dataT={dataT}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    totalRecords={totalRecords}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomBlockingList;
